// The diary editor's photo attachments.
//
// W54.0 A1 moved it out of `components/diary/DiaryEditor.tsx` when that file
// was split to get under the `AGENTS.md §3 #10` ceiling.
//
// Removing a thumbnail here removes the attachment from **this draft** only.
// The media row and its `AlbumItem` stay: the photo is in an album now, and
// deleting it here would delete something the user can still see in 相簿. Undo
// from the album's own screen.
//
// **44×44 hit area, 24×24 visual.** The first pass sized the whole button to
// the 24px badge it draws, which is what a thumbnail overlay usually looks
// like and is still a 24pt touch target — under Apple's 44pt floor and the
// reason `mobile-components` lists "touch targets too small" as its first
// common mistake. The glyph stays small; the tappable box does not.

import { ImagePlus, X } from "lucide-react";

import { mediaFileUrl } from "@/lib/api/album";
import type { DiaryAttachment } from "@/lib/diary/formState";

export interface DiaryPhotoAttachProps {
  media: DiaryAttachment[];
  onRemove: (id: string) => void;
  onAdd: () => void;
  disabled?: boolean;
}

export function DiaryPhotoAttach({
  media,
  onRemove,
  onAdd,
  disabled,
}: DiaryPhotoAttachProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-label text-ink-2">相片</span>
      <div className="flex flex-wrap items-center gap-2">
        {media.map((m) => (
          <span
            key={m.id}
            data-testid={`diary-attached-${m.id}`}
            className="relative h-20 w-20 overflow-hidden rounded-lg bg-paper-deep"
          >
            <img
              src={mediaFileUrl(m.id)}
              alt="已附加的相片"
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              aria-label="移除附加的相片"
              onClick={() => onRemove(m.id)}
              disabled={disabled}
              className="press absolute right-0 top-0 inline-flex h-11 w-11 items-center justify-center disabled:opacity-50"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink-1/60 text-paper">
                <X size={14} aria-hidden />
              </span>
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          data-testid="diary-add-photo"
          className="press inline-flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-hairline text-ink-3 hover:bg-black/5 disabled:opacity-50"
        >
          <ImagePlus size={20} strokeWidth={1.5} aria-hidden />
          <span className="text-micro">新增</span>
        </button>
      </div>
    </div>
  );
}
